// @secretaa/sdk — cửa DUY NHẤT để FE gọi mạng (I12: FE cấm fetch / axios trực tiếp). Gọi BFF cùng origin,
// cookie HttpOnly đi kèm, header CSRF đọc từ cookie `saa_csrf` (double-submit).
import { ErrorBody } from "@secretaa/http-contract";
import type { z } from "zod";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ClientOptions {
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  readonly csrfToken?: () => string | undefined;
}

export interface RequestOptions<S extends z.ZodType> {
  readonly method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  readonly body?: unknown;
  readonly schema: S;
  readonly headers?: Record<string, string>;
  readonly signal?: AbortSignal;
  /** Caller-owned, request-specific memory snapshot; never an offline fallback. */
  readonly revalidate?: { readonly path:string; readonly etag:string; readonly body:unknown };
}

export function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return m ? decodeURIComponent(m.slice(name.length + 1)) : undefined;
}

export function createClient(options: ClientOptions = {}) {
  const base = options.baseUrl ?? "";
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const csrf = options.csrfToken ?? (() => readCookie("saa_csrf"));

  async function request<S extends z.ZodType>(path: string, opts: RequestOptions<S>): Promise<{ body: z.infer<S>; etag: string | null }> {
    const method = opts.method ?? "GET";
    const previous=opts.revalidate?opts.schema.safeParse(opts.revalidate.body):undefined;
    if(opts.revalidate&&(method!=="GET"||opts.body!==undefined||opts.revalidate.path!==path||!previous?.success||opts.revalidate.etag.length>256||!/^(?:W\/)?"[\x21\x23-\x7e]{1,128}"$/.test(opts.revalidate.etag)))throw new ApiError(422,"VALIDATION_FAILED","Invalid conditional snapshot");
    const headers: Record<string, string> = { accept: "application/json", ...opts.headers };
    if(opts.revalidate)headers["if-none-match"]=opts.revalidate.etag;
    if (opts.body !== undefined) headers["content-type"] = "application/json";
    if (method !== "GET") {
      const token = csrf();
      if (token) headers["x-csrf-token"] = token;
    }
    const res = await doFetch(`${base}${path}`, {
      method,
      headers,
      credentials: "include",
      body: opts.body === undefined ? null : JSON.stringify(opts.body),
      signal: opts.signal ?? null,
    });
    const text = await res.text();
    opts.signal?.throwIfAborted();
    if(res.status===304){
      if(!opts.revalidate||!previous?.success||text!==""||res.headers.get("etag")!==opts.revalidate.etag)throw new ApiError(502,"CONTRACT_MISMATCH","Invalid conditional response");
      return {body:previous.data as z.infer<S>,etag:opts.revalidate.etag};
    }
    let json: unknown = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        throw new ApiError(res.status, "BAD_RESPONSE", "Response không phải JSON");
      }
    }
    if (!res.ok) {
      const parsed = ErrorBody.safeParse(json);
      if (parsed.success) {
        const e = parsed.data.error;
        throw new ApiError(res.status, e.code, e.message, e.requestId, e.details);
      }
      throw new ApiError(res.status, "INTERNAL_ERROR", `HTTP ${res.status}`);
    }
    const parsed = opts.schema.safeParse(json);
    if (!parsed.success) throw new ApiError(res.status, "CONTRACT_MISMATCH", "Response lệch contract");
    return { body: parsed.data as z.infer<S>, etag: res.headers.get("etag") };
  }

  return { request };
}

export type SecretaaClient = ReturnType<typeof createClient>;
